// src/main/Dashboard.js (Updated Version)

import React, { useState, useEffect } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Box,
    Avatar,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Divider,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    AttachMoney,
    People,
    Home,
    MeetingRoom,
    Receipt,
    Build,
    ArrowUpward,
    ArrowDownward,
    Refresh,
    CheckCircle,
    Business,
    MoneyOff,
    NotificationsActive
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import useCustomToast from '../../hooks/useToast';
import {
    APIDashboardStats,
    APIDashboardChartData,
    APIDashboardRecentActivities,
    APIDashboardQuickActions
} from '../../apiServices/APIs';
import { formatCreatedAtDate } from '../../utils/helpers';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorrState';
import LoadingState from '../../components/LoadingState';
import { usePermission } from '../../hooks/usePermission';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ArcElement, PointElement, LineElement, Filler);

const Dashboard = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [recentActivities, setRecentActivities] = useState([]);
    const [quickActions, setQuickActions] = useState([]);
    const showToast = useCustomToast();
    const { hasPermission } = usePermission();

    useEffect(() => {
        fetchDashboardData();
        
        const interval = setInterval(() => {
            fetchDashboardData();
        }, 60000); // Refresh every 60 seconds
        
        return () => clearInterval(interval);
    }, []);

    const fetchDashboardData = async () => {
        setLoading(true);
        setError(null);
        try {
            const userBranchId = getUserDetails("branchId");
            
            const [statsRes, chartRes, activitiesRes, actionsRes] = await Promise.allSettled([
                apiService.listStats(userBranchId, APIDashboardStats),
                apiService.listStats(userBranchId, APIDashboardChartData),
                apiService.list(userBranchId, APIDashboardRecentActivities),
                apiService.list(userBranchId, APIDashboardQuickActions)
            ]);
            
            if (statsRes.status === 'fulfilled' && statsRes.value.success) {
                setStats(statsRes.value.data);
            }
            if (chartRes.status === 'fulfilled' && chartRes.value.success) {
                setChartData(chartRes.value.data);
            }
            if (activitiesRes.status === 'fulfilled' && activitiesRes.value.success) {
                setRecentActivities(Array.isArray(activitiesRes.value.data) ? activitiesRes.value.data : []);
            }
            if (actionsRes.status === 'fulfilled' && actionsRes.value.success) {
                setQuickActions(Array.isArray(actionsRes.value.data) ? actionsRes.value.data : []);
            }
        } catch (error) {
            console.error("Dashboard error:", error);
            setError("Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-UG', {
            style: 'currency',
            currency: 'UGX',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Chart configurations
    const occupancyChartData = {
        labels: ['Occupied', 'Vacant', 'Maintenance'],
        datasets: [{
            data: [
                stats?.occupied_units || 0,
                stats?.vacant_units || 0,
                stats?.maintenance_units || 0
            ],
            backgroundColor: ['#4caf50', '#f44336', '#ff9800'],
            borderWidth: 0
        }]
    };

    const revenueChartData = {
        labels: chartData?.months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        datasets: [
            {
                label: 'Revenue',
                data: chartData?.revenue || [],
                borderColor: '#4caf50',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 3
            },
            {
                label: 'Expenses',
                data: chartData?.expenses || [],
                borderColor: '#f44336',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                fill: true,
                tension: 0.4,
                pointRadius: 3
            }
        ]
    };

    const collectionChartData = {
        labels: chartData?.collection_months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [{
            label: 'Collection Rate (%)',
            data: chartData?.collection_rates || [],
            backgroundColor: 'rgba(33, 150, 243, 0.7)',
            borderColor: '#2196f3',
            borderWidth: 1,
            borderRadius: 4
        }]
    };

    const occupancyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const total = (stats?.total_units || 1);
                        const percentage = ((context.raw / total) * 100).toFixed(1);
                        return `${context.label}: ${context.raw} units (${percentage}%)`;
                    }
                }
            }
        }
    };

    const revenueOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${formatCurrency(context.raw)}`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatCurrency(value)
                }
            }
        }
    };

    const collectionOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `Collection Rate: ${context.raw}%`
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'Collection Rate (%)'
                }
            }
        }
    };

    const StatCard = ({ title, value, icon, color, trend, onClick, suffix = '' }) => (
        <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }} style={{ height: '100%' }}>
            <Paper 
                elevation={2} 
                onClick={onClick}
                sx={{ 
                    p: 2, 
                    borderRadius: 3,
                    cursor: onClick ? 'pointer' : 'default',
                    transition: 'box-shadow 0.3s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    '&:hover': onClick ? { boxShadow: 6 } : {}
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
                            {title}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette[color]?.main || 'text.primary', whiteSpace: 'nowrap' }}>
                            {value}{suffix}
                        </Typography>
                        {trend !== undefined && trend !== null && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                {trend > 0 ? (
                                    <ArrowUpward sx={{ fontSize: 14 }} color="success" />
                                ) : trend < 0 ? (
                                    <ArrowDownward sx={{ fontSize: 14 }} color="error" />
                                ) : null}
                                <Typography variant="caption" color={trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'text.secondary'} sx={{ whiteSpace: 'nowrap' }}>
                                    {trend !== 0 ? `${Math.abs(trend)}% vs last month` : 'No change'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Avatar sx={{ bgcolor: `${color}.light`, color: `${color}.main`, width: 56, height: 56, flexShrink: 0, ml: 1 }}>
                        {icon}
                    </Avatar>
                </Box>
            </Paper>
        </motion.div>
    );

    const getActivityIcon = (type) => {
        switch (type) {
            case 'Payment': return <AttachMoney />;
            case 'Tenant': return <People />;
            case 'Maintenance': return <Build />;
            default: return <Receipt />;
        }
    };

    const getActivityColor = (type) => {
        switch (type) {
            case 'Payment': return 'success';
            case 'Tenant': return 'primary';
            case 'Maintenance': return 'warning';
            default: return 'info';
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'active': return 'success';
            case 'in_progress': return 'warning';
            case 'reported': return 'info';
            default: return 'default';
        }
    };

    if (loading && !stats) {
        return <LoadingState message="Loading Dashboard..." variant="medical" size="large" fullPage />;
    }

    if (error && !stats) {
        return <ErrorState error={error} onRetry={fetchDashboardData} title="Unable to Load Dashboard" variant="medical" fullPage retryText="Retry Loading" />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Welcome Section */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Paper 
                    elevation={2} 
                    sx={{ 
                        p: 3, 
                        mb: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, 
                        color: 'white' 
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Welcome back, {getUserDetails("userName") || 'Admin'}!
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Here's your property management overview for today.
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
                            <Tooltip title="Refresh Dashboard">
                                <IconButton 
                                    sx={{ 
                                        color: 'white', 
                                        bgcolor: 'rgba(255,255,255,0.2)', 
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } 
                                    }} 
                                    onClick={fetchDashboardData}
                                >
                                    <Refresh />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Paper>
            </motion.div>

            {/* Stats Row 1 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Properties" 
                        value={stats?.total_properties || 0} 
                        icon={<Business sx={{ fontSize: 32 }} />} 
                        color="primary"
                        onClick={() => navigate('/properties')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Units" 
                        value={stats?.total_units || 0} 
                        icon={<MeetingRoom sx={{ fontSize: 32 }} />} 
                        color="info"
                        onClick={() => navigate('/floor-units')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Total Tenants" 
                        value={stats?.total_tenants || 0} 
                        icon={<People sx={{ fontSize: 32 }} />} 
                        color="success"
                        onClick={() => navigate('/tenants')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Occupancy Rate" 
                        value={stats?.occupancy_rate || 0} 
                        icon={<Home sx={{ fontSize: 32 }} />} 
                        color="warning"
                        suffix="%"
                        onClick={() => navigate('/floor-units')}
                    />
                </Grid>
            </Grid>

            {/* Stats Row 2 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Monthly Revenue" 
                        value={formatCurrency(stats?.monthly_revenue)} 
                        icon={<AttachMoney sx={{ fontSize: 32 }} />} 
                        color="success"
                        trend={stats?.revenue_trend}
                        onClick={() => navigate('/reports')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Monthly Expenses" 
                        value={formatCurrency(stats?.monthly_expenses)} 
                        icon={<MoneyOff sx={{ fontSize: 32 }} />} 
                        color="error"
                        onClick={() => navigate('/expenses')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Net Profit" 
                        value={formatCurrency(stats?.net_profit)} 
                        icon={<TrendingUp sx={{ fontSize: 32 }} />} 
                        color="primary"
                        onClick={() => navigate('/reports')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard 
                        title="Collection Rate" 
                        value={stats?.collection_rate || 0} 
                        icon={<Receipt sx={{ fontSize: 32 }} />} 
                        color="info"
                        suffix="%"
                        onClick={() => navigate('/reports')}
                    />
                </Grid>
            </Grid>

            {/* Charts Section - Horizontally Aligned */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Chart 1: Revenue vs Expenses */}
                <Grid item xs={12} md={4}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: 350, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Revenue vs Expenses
                            </Typography>
                            <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 0 }}>
                                <Line data={revenueChartData} options={revenueOptions} />
                            </Box>
                        </Paper>
                    </motion.div>
                </Grid>

                {/* Chart 2: Occupancy Distribution */}
                <Grid item xs={12} md={4}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: 350, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Occupancy Distribution
                            </Typography>
                            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative', minHeight: 0, mb: 2 }}>
                                <Box sx={{ width: '100%', height: '100%' }}>
                                    <Doughnut data={occupancyChartData} options={occupancyOptions} />
                                </Box>
                            </Box>
                            <Grid container spacing={1} sx={{ mt: 'auto' }}>
                                <Grid item xs={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#4caf50' }} />
                                        <Typography variant="caption" noWrap>Occ: {stats?.occupied_units || 0}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f44336' }} />
                                        <Typography variant="caption" noWrap>Vac: {stats?.vacant_units || 0}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff9800' }} />
                                        <Typography variant="caption" noWrap>Maint: {stats?.maintenance_units || 0}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </motion.div>
                </Grid>

                {/* Chart 3: Collection Rate Trend */}
                <Grid item xs={12} md={4}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: 350, display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Collection Rate Trend
                            </Typography>
                            <Box sx={{ flexGrow: 1, position: 'relative', minHeight: 0, mb: 2 }}>
                                <Bar data={collectionChartData} options={collectionOptions} />
                            </Box>
                            <Box sx={{ p: 1.5, bgcolor: `${theme.palette.primary.main}15`, borderRadius: 2, mt: 'auto' }}>
                                <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                    Average Collection Rate: {stats?.collection_rate || 0}%
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={stats?.collection_rate || 0} 
                                    sx={{ mt: 0.5, height: 6, borderRadius: 3 }} 
                                />
                            </Box>
                        </Paper>
                    </motion.div>
                </Grid>
            </Grid>

            {/* Quick Actions & Alerts Section - Horizontally Aligned */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Quick Actions */}
                <Grid item xs={12} md={6}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: 320, overflow: 'auto' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Quick Actions
                            </Typography>
                            <Grid container spacing={2}>
                                {quickActions.map((action, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                        <Button 
                                            fullWidth 
                                            variant="outlined" 
                                            onClick={() => navigate(action.path)} 
                                            sx={{ 
                                                py: 1.5, 
                                                justifyContent: 'flex-start',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                borderRadius: 2
                                            }}
                                        >
                                            <Typography component="span" sx={{ mr: 1 }}>{action.icon}</Typography>
                                            {action.label}
                                        </Button>
                                    </Grid>
                                ))}
                                {quickActions.length === 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                                            No actions available
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    </motion.div>
                </Grid>

                {/* Alerts */}
                <Grid item xs={12} md={6}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 3, height: 320, overflow: 'auto' }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                <NotificationsActive sx={{ mr: 1, fontSize: 20 }} />
                                Alerts
                            </Typography>
                            <List dense>
                                {stats?.alerts?.map((alert, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem sx={{ px: 1, borderRadius: 1, mb: 0.5 }}>
                                            <ListItemAvatar>
                                                <Avatar 
                                                    sx={{ 
                                                        width: 32, 
                                                        height: 32,
                                                        bgcolor: alert.severity === 'error' ? 'error.light' : 
                                                                 alert.severity === 'warning' ? 'warning.light' : 'info.light',
                                                        color: alert.severity === 'error' ? 'error.main' : 
                                                               alert.severity === 'warning' ? 'warning.main' : 'info.main'
                                                    }}
                                                >
                                                    <Typography variant="body2">{alert.icon}</Typography>
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText 
                                                primary={alert.message} 
                                                secondary={alert.date}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                            />
                                        </ListItem>
                                        {index < stats.alerts.length - 1 && <Divider component="li" />}
                                    </React.Fragment>
                                ))}
                                {(!stats?.alerts || stats.alerts.length === 0) && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                        <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                                        <Typography variant="body2" color="textSecondary" textAlign="center">
                                            All clear! No alerts at this time.
                                        </Typography>
                                    </Box>
                                )}
                            </List>
                        </Paper>
                    </motion.div>
                </Grid>
            </Grid>

            {/* Recent Activities - Bottom */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Recent Activities
                        </Typography>
                        <Button 
                            size="small" 
                            onClick={fetchDashboardData}
                            startIcon={<Refresh />}
                        >
                            Refresh
                        </Button>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                    <TableCell>Activity</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Details</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recentActivities.map((activity, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 28, height: 28, bgcolor: `${getActivityColor(activity.type)}.light`, color: `${getActivityColor(activity.type)}.main` }}>
                                                    {getActivityIcon(activity.type)}
                                                </Avatar>
                                                <Typography variant="body2" fontWeight={500}>{activity.title}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                size="small" 
                                                label={activity.type} 
                                                color={getActivityColor(activity.type)}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                                {activity.description}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="textSecondary">
                                                {formatCreatedAtDate(activity.date)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {activity.status && (
                                                <Chip 
                                                    size="small" 
                                                    label={activity.status} 
                                                    color={getStatusColor(activity.status)}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {recentActivities.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                            <Typography variant="body2" color="textSecondary">
                                                No recent activities to display
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </motion.div>
        </Container>
    );
};

export default Dashboard;